const sanitizeCookieValue = (name, value) => {
  if (value === null || value === undefined) return "";

  const stringValue = typeof value === "string" ? value : String(value);

  if (stringValue.startsWith('{') || stringValue.startsWith('[')) {
    try {
      JSON.parse(stringValue);
      return stringValue;
    } catch (e) {
      console.error(`Invalid JSON in cookie ${name}:`, e);
    }
  }

  return stringValue.replace(/[<>'"]/g, "");
};

const getDomain = () => {
  const host_domain = location.hostname;
  const allowed_domains = ["deriv.com", "deriv.ae", "binary.sx"];
  return allowed_domains.find(d => host_domain.includes(d)) ?? host_domain;
};

const getCookie = (name) => {
  if (!name) return null;

  try {
    const encodedName = encodeURIComponent(name);

    for (let cookie of document.cookie.split(';')) {
      cookie = cookie.trim();

      if (cookie.startsWith(encodedName + '=')) {
        return decodeURIComponent(cookie.substring(encodedName.length + 1));
      }

      if (cookie.startsWith(name + '=')) {
        const value = cookie.substring(name.length + 1);
        try {
          return decodeURIComponent(value);
        } catch (e) {
          return value;
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Failed to get cookie:', error);
    return null;
  }
};

const setCookieBase = (name, value, options = {}) => {
  const sanitizedValue = sanitizeCookieValue(name, value);
  const config = {
    expires: 365,
    domain: getDomain(),
    path: '/',
    sameSite: 'None',
    secure: true,
    ...options
  };

  let expiresString = '';
  if (config.expires) {
    const date = new Date();
    if (typeof config.expires === 'number') {
      date.setTime(date.getTime() + (config.expires * 24 * 60 * 60 * 1000));
    } else if (config.expires instanceof Date) {
      date.setTime(config.expires.getTime());
    }
    expiresString = `; expires=${date.toUTCString()}`;
  }

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(sanitizedValue)}${expiresString}`;
  if (config.domain) cookieString += `; domain=${config.domain}`;
  if (config.path) cookieString += `; path=${config.path}`;
  if (config.sameSite) cookieString += `; SameSite=${config.sameSite}`;
  if (config.secure) cookieString += `; Secure`;

  try {
    document.cookie = cookieString;
  } catch (error) {
    console.error('Failed to set cookie:', error);
  }

  return sanitizedValue;
};

function DerivMarketingCookies() {
  let cookieData = { original: {}, sanitized: {} };
  window.marketingCookieLogs = [];
  window.marketingCookies = {};

  const searchParams = new URLSearchParams(window.location.search);
  let utm_data = {};
  let affiliate_tracking = null;
  let overwrite_happened = false;

  const hasAffiliateParams = searchParams.has("t") || searchParams.has("affiliate_token") || searchParams.has("sidc") || searchParams.has("sidi");
  const isPartnerToken = searchParams.has("sidi");

  const utm_fields = [
    "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
    "utm_ad_id", "utm_click_id", "utm_adgroup_id", "utm_campaign_id",
    "utm_msclk_id", "utm_fbcl_id", "utm_ttclid", "utm_sccid",
    ["msclkid", "utm_msclk_id"],
    ["fbclid", "utm_fbcl_id"],
    ["ttclid", "utm_ttclid"],
    ["ScCid", "utm_sccid"],
  ];

  const log = (action, details) => {
    let timestamp;
    try {
      timestamp = new Date().toISOString();
    } catch (e) {
      timestamp = new Date(Date.now()).toISOString();
    }
    window.marketingCookieLogs.push({ timestamp, action, details });
  };

  const setCookie = (name, value, options = {}) => {
    const sanitizedValue = setCookieBase(name, value, options);

    const verification = getCookie(name);
    if (verification !== sanitizedValue) {
      console.warn(`Cookie verification failed for ${name}. Expected: ${sanitizedValue}, Got: ${verification}`);
    }

    window.marketingCookies[name] = value;
    cookieData.original[name] = value;
    cookieData.sanitized[name] = sanitizedValue;

    return verification === sanitizedValue;
  };

  const eraseCookie = (name) => {
    const pastDate = new Date(0).toUTCString();
    const domain = getDomain();

    try {
      document.cookie = `${encodeURIComponent(name)}=; expires=${pastDate}; domain=${domain}; path=/; SameSite=None; Secure`;
      document.cookie = `${encodeURIComponent(name)}=; expires=${pastDate}; path=/; SameSite=None; Secure`;
      document.cookie = `${encodeURIComponent(name)}=; expires=${pastDate}; domain=${domain}; path=/`;
      document.cookie = `${encodeURIComponent(name)}=; expires=${pastDate}; path=/`;
    } catch (error) {
      console.error('Failed to erase cookie:', error);
    }

    delete window.marketingCookies[name];
    delete cookieData.original[name];
    delete cookieData.sanitized[name];
  };

  const isMobile = () =>
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const toISOFormat = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return "";
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const shouldOverwrite = (new_utm_data, current_utm_data) => {
    const valid_new_utm_source = new_utm_data.utm_source && new_utm_data.utm_source !== "null";
    if (!current_utm_data && valid_new_utm_source) return true;

    const required_fields = ["utm_source", "utm_medium", "utm_campaign"];
    return required_fields.every(field => new_utm_data[field]);
  };

  const cleanAllMarketingCookies = (reason) => {
    [
      "utm_data", "affiliate_tracking", "affiliate_data", "affiliate_token_type",
      "signup_device", "date_first_contact", "gclid", "campaign_channel"
    ].forEach(cookieName => eraseCookie(cookieName));

    window.marketingCookies = {};
    cookieData.original = {};
    cookieData.sanitized = {};

    console.error("🚨 AFFILIATE LINK ERROR:", reason);
    log("affiliate_validation", { error: reason, action: "cookies_cleaned" });
  };

  const hasValidAffiliateToken = () => {
    if (getCookie("affiliate_tracking")) return true;
    const affiliateData = getCookie("affiliate_data");
    if (affiliateData) {
      try {
        const parsed = JSON.parse(affiliateData);
        return !!(parsed.affiliate_token || parsed.affiliate_tracking);
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  /* UTMs */
  const utm_data_cookie = getCookie("utm_data");
  let current_utm_data = {};

  if (utm_data_cookie) {
    try {
      current_utm_data = JSON.parse(utm_data_cookie);
    } catch (e) {
      try {
        current_utm_data = JSON.parse(decodeURIComponent(utm_data_cookie));
      } catch (e2) {
        console.error("Failed to parse utm_data cookie:", e2);
      }
    }
  }

  if (current_utm_data?.utm_medium === "affiliate" && !hasAffiliateParams && !hasValidAffiliateToken()) {
    cleanAllMarketingCookies("Existing utm_medium=affiliate but missing affiliate parameters and no valid affiliate cookies");
    return cookieData;
  }

  let new_utm_data = {};

  if (!current_utm_data?.utm_source && !searchParams.toString()) {
    new_utm_data = { utm_source: document.referrer || "null" };
  }

  utm_fields.forEach(field => {
    if (Array.isArray(field)) {
      const [field_key, mapped_field_value] = field;
      if (searchParams.has(field_key)) {
        new_utm_data[mapped_field_value] = searchParams.get(field_key).substring(0, 200);
      }
    } else if (searchParams.has(field)) {
      new_utm_data[field] = searchParams.get(field).substring(0, 100);
    }
  });

  /* STP */
  const stp_fields = [
    "gclid", "wbraid", "gbraid", "ttclid", "msclkid",
    ["ScCid", "scclid"],
  ];
  const new_stp_data = {};

  stp_fields.forEach(field => {
    if (Array.isArray(field)) {
      const [field_key, mapped_field_value] = field;
      if (searchParams.has(field_key)) {
        new_stp_data[mapped_field_value] = searchParams.get(field_key).substring(0, 200);
      }
    } else if (searchParams.has(field)) {
      new_stp_data[field] = searchParams.get(field).substring(0, 100);
    }
  });

  if (Object.keys(new_stp_data).length > 0) {
    eraseCookie("stp_data");
    setCookie("stp_data", JSON.stringify(new_stp_data));
  }

  if (new_utm_data.utm_medium === "affiliate" && !hasAffiliateParams) {
    cleanAllMarketingCookies("New utm_medium=affiliate but missing affiliate parameters (t, affiliate_token, or sidc)");
    return cookieData;
  }

  const landing_page_url = location.origin + location.pathname;

  if (shouldOverwrite(new_utm_data, current_utm_data)) {
    eraseCookie("affiliate_tracking");
    eraseCookie("affiliate_data");
    eraseCookie("affiliate_token_type");
    eraseCookie("utm_data");
    setCookie("utm_data", JSON.stringify(new_utm_data));
    setCookie("landing_page_url", landing_page_url);
    overwrite_happened = true;
    utm_data = new_utm_data;
  } else {
    utm_data = { ...current_utm_data, ...new_utm_data };
    if (Object.keys(new_utm_data).length > 0) {
      setCookie("utm_data", JSON.stringify(utm_data));
      setCookie("landing_page_url", landing_page_url);
    }
  }

  /* Affiliate tracking */
  log("affiliate_tracking", {
    action: "started",
    hasAffiliateParams,
    searchParams: {
      t: searchParams.get("t"),
      affiliate_token: searchParams.get("affiliate_token"),
      sidc: searchParams.get("sidc"),
      sidi: searchParams.get("sidi")
    },
    overwrite_happened
  });

  if (hasAffiliateParams) {
    const affiliateValue = searchParams.get("t") ||
      searchParams.get("affiliate_token") ||
      searchParams.get("sidc") ||
      searchParams.get("sidi");

    log("affiliate_tracking", {
      action: "affiliate_params_found",
      affiliateValue,
      source: searchParams.has("t") ? "t" :
        searchParams.has("affiliate_token") ? "affiliate_token" : "sidc" || "sidi"
    });

    eraseCookie("affiliate_tracking");
    setCookie("affiliate_tracking", affiliateValue);
    setCookie("affiliate_token_type", isPartnerToken ? "partner" : "client");
    affiliate_tracking = affiliateValue;

    log("affiliate_tracking", {
      action: "affiliate_set",
      affiliateValue,
      cookie_verification: getCookie("affiliate_tracking")
    });
  } else {
    log("affiliate_tracking", {
      action: "no_affiliate_params",
      existing_affiliate_cookie: getCookie("affiliate_tracking")
    });
  }

  log("affiliate_tracking", {
    action: "completed",
    final_affiliate_tracking: affiliate_tracking,
    final_cookie_value: getCookie("affiliate_tracking")
  });

  /* signup_device */
  const signup_device_cookie_unparsed = getCookie("signup_device") || "{}";
  let signup_device_cookie = {};

  try {
    signup_device_cookie = JSON.parse(signup_device_cookie_unparsed);
  } catch (e) {
    try {
      signup_device_cookie = JSON.parse(
        decodeURI(signup_device_cookie_unparsed).replaceAll("%2C", ",").replaceAll("%3A", ":")
      );
    } catch (e2) {
      console.error("Failed to parse signup_device cookie:", e2);
    }
  }

  if (!signup_device_cookie.signup_device) {
    setCookie("signup_device", JSON.stringify({ signup_device: isMobile() ? "mobile" : "desktop" }));
  } else {
    cookieData.original.signup_device = signup_device_cookie.signup_device;
    cookieData.sanitized.signup_device = signup_device_cookie.signup_device;
  }

  /* date_first_contact */
  const date_first_contact_cookie_unparsed = getCookie("date_first_contact") || "{}";
  let date_first_contact_cookie = {};

  try {
    date_first_contact_cookie = JSON.parse(date_first_contact_cookie_unparsed);
  } catch (e) {
    try {
      date_first_contact_cookie = JSON.parse(
        decodeURI(date_first_contact_cookie_unparsed).replaceAll("%2C", ",").replaceAll("%3A", ":")
      );
    } catch (e2) {
      console.error("Failed to parse date_first_contact cookie:", e2);
    }
  }

  if (!date_first_contact_cookie.date_first_contact) {
    setCookie("date_first_contact", JSON.stringify({
      date_first_contact: toISOFormat(new Date(Math.floor(Date.now() / 1000) * 1000))
    }));
  } else {
    cookieData.original.date_first_contact = date_first_contact_cookie.date_first_contact;
    cookieData.sanitized.date_first_contact = date_first_contact_cookie.date_first_contact;
  }

  /* gclid */
  const final_gclid = searchParams.get("gclid") || searchParams.get("gclid_url") || "";
  if (final_gclid) {
    eraseCookie("gclid");
    setCookie("gclid", final_gclid);
  }

  /* campaign_channel */
  const campaign_channel = searchParams.get("ca");
  if (campaign_channel) {
    eraseCookie("campaign_channel");
    setCookie("campaign_channel", campaign_channel);
  }

  /* prclid */
  const prclid = searchParams.get("prclid");
  if (prclid) {
    eraseCookie("prclid");
    setCookie("prclid", prclid);
  }

  /* affiliate_data */
  log("affiliate_data", {
    action: "started",
    hasAffiliateParams,
    affiliate_tracking,
    utm_data_cookie: getCookie("utm_data")
  });

  if (hasAffiliateParams) {
    const combined_affiliate_data = { affiliate_token: affiliate_tracking, ...utm_data };

    log("affiliate_data", { action: "creating_combined_data", combined_affiliate_data, utm_data, affiliate_tracking });

    eraseCookie("affiliate_data");
    setCookie("affiliate_data", JSON.stringify(combined_affiliate_data));

    log("affiliate_data", { action: "affiliate_data_set", cookie_verification: getCookie("affiliate_data") });
  } else {
    log("affiliate_data", { action: "no_affiliate_params_skipping_affiliate_data" });
  }

  /* final affiliate validation */
  const current_utm_medium = utm_data.utm_medium || current_utm_data?.utm_medium;

  log("affiliate_validation", {
    action: "final_validation_check",
    current_utm_medium,
    hasAffiliateParams,
    utm_data,
    current_utm_data,
    affiliate_tracking_cookie: getCookie("affiliate_tracking"),
    utm_data_cookie: getCookie("utm_data"),
    affiliate_data_cookie: getCookie("affiliate_data")
  });

  if (current_utm_medium === "affiliate" && !hasAffiliateParams) {
    if (!hasValidAffiliateToken()) {
      log("affiliate_validation", {
        action: "validation_failed_cleaning_cookies",
        reason: "utm_medium=affiliate but missing affiliate parameters and no valid affiliate cookies",
        current_utm_medium,
        hasAffiliateParams,
      });
      cleanAllMarketingCookies("utm_medium=affiliate but missing affiliate parameters and no valid affiliate cookies");
    } else {
      log("affiliate_validation", {
        action: "validation_passed_with_existing_token",
        current_utm_medium,
        hasAffiliateParams,
      });
    }
  } else {
    log("affiliate_validation", { action: "validation_passed", current_utm_medium, hasAffiliateParams });
  }

  /* populate cookieData from existing cookies */
  const marketingCookieNames = [
    "utm_data", "affiliate_tracking", "affiliate_data", "affiliate_token_type",
    "signup_device", "date_first_contact", "gclid", "campaign_channel", "prclid", "rudder_anonymous_id"
  ];

  marketingCookieNames.forEach(cookieName => {
    const existingValue = getCookie(cookieName);
    if (existingValue !== null && !cookieData.original[cookieName]) {
      cookieData.original[cookieName] = existingValue;
      cookieData.sanitized[cookieName] = sanitizeCookieValue(cookieName, existingValue);
      window.marketingCookies[cookieName] = existingValue;
    }
  });

  return cookieData;
}

function addStpCookieData() {
  const stpDataCookie = getCookie('stp_data');
  let stpData = {};

  if (stpDataCookie) {
    try {
      stpData = JSON.parse(stpDataCookie);
    } catch (e) {
      console.warn('Failed to parse existing stp_data cookie, starting fresh:', e);
    }
  }

  const fbc_cookie = getCookie("_fbc");
  const fbp_cookie = getCookie("_fbp");
  const ga_cookie = getCookie("_ga");

  if (fbc_cookie) stpData.fbc = fbc_cookie;
  if (fbp_cookie) stpData.fbp = fbp_cookie;
  if (ga_cookie) stpData._ga = ga_cookie;

  const gaMeasurementCookieName = location.hostname.includes("deriv.ae") ? "_ga_F3QTR4CDHR" : "_ga_R0D2Z1965W";
  const gaMeasurementCookie = getCookie(gaMeasurementCookieName);
  if (gaMeasurementCookie) {
    stpData._ga_measurement_id = gaMeasurementCookieName;
    stpData._ga_measurement_value = gaMeasurementCookie;
  }

  setCookieBase("stp_data", JSON.stringify(stpData));
}

DerivMarketingCookies();
addStpCookieData();

window.getMarketingCookies = () => DerivMarketingCookies();
window.addStpCookieData = addStpCookieData;

const getMarketingCookiesPayloadData = () => {
  const result = {};

  const parseJsonCookie = (cookieName, propertyName = null) => {
    const cookieValue = getCookie(cookieName);
    if (!cookieValue) return null;
    try {
      const parsed = JSON.parse(cookieValue);
      if (!parsed || (typeof parsed === 'object' && Object.keys(parsed).length === 0)) return null;
      return propertyName ? parsed[propertyName] || null : parsed;
    } catch (e) {
      console.error(`Failed to parse ${cookieName} cookie:`, e);
      return null;
    }
  };

  const getStringCookie = (cookieName) => getCookie(cookieName) || null;

  const cookieConfig = [
    { cookieName: 'utm_data', resultKey: 'utm_data', parser: parseJsonCookie },
    { cookieName: 'date_first_contact', resultKey: 'date_first_contact', parser: parseJsonCookie, property: 'date_first_contact' },
    { cookieName: 'signup_device', resultKey: 'signup_device', parser: parseJsonCookie, property: 'signup_device' },
    { cookieName: 'landing_page_url', resultKey: 'landing_page_url', parser: getStringCookie },
    { cookieName: 'campaign_channel', resultKey: 'campaign_channel', parser: getStringCookie },
    { cookieName: 'prclid', resultKey: 'prclid', parser: getStringCookie },
    { cookieName: 'rudder_anonymous_id', resultKey: 'rudder_anonymous_id', parser: getStringCookie }
  ];

  cookieConfig.forEach(({ cookieName, resultKey, parser, property }) => {
    try {
      const value = property ? parser(cookieName, property) : parser(cookieName);
      if (value !== null) result[resultKey] = value;
    } catch (error) {
      console.error(`Error processing cookie ${cookieName}:`, error);
    }
  });

  try {
    const affiliateToken = parseJsonCookie('affiliate_data', 'affiliate_token');
    if (affiliateToken) result.affiliate_data = { token: affiliateToken };
  } catch (error) {
    console.error('Error processing affiliate_data:', error);
  }

  try {
    addStpCookieData();
    const stapeData = parseJsonCookie('stp_data');
    if (stapeData) result.marketing_analytics_data = stapeData;
  } catch (error) {
    console.error('Error processing stp_data:', error);
  }

  return result;
};

window.getMarketingCookiesPayloadData = getMarketingCookiesPayloadData;
